import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { motion } from "framer-motion";
import { GoArrowDownRight, GoDotFill } from "react-icons/go";

import bg from "../../assets/bg_video.mp4";
import NameSVG from "../../assets/images/NameSVG";

const revealEase = [0.22, 1, 0.36, 1];
const detailsRevealDelay = 520;

const wrapperVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.65,
      ease: revealEase,
    },
  },
};

export default function Home() {
  const [canReveal, setCanReveal] = useState(() =>
    typeof window === "undefined" ? true : window.__introTransitionDone,
  );
  const [nameVisible, setNameVisible] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);

  const disableImage = (event) => event.preventDefault();

  useEffect(() => {
    if (canReveal) {
      return undefined;
    }

    const showHome = () => {
      setCanReveal(true);
    };

    window.addEventListener("intro-transition:complete", showHome);

    return () => {
      window.removeEventListener("intro-transition:complete", showHome);
    };
  }, [canReveal]);

  useEffect(() => {
    if (!canReveal) {
      return undefined;
    }

    window.__homeDetailsRevealStarted = false;
    setNameVisible(false);
    setDetailsVisible(false);

    const revealFrame = window.requestAnimationFrame(() => {
      setNameVisible(true);
    });

    const detailsTimer = window.setTimeout(() => {
      window.__homeDetailsRevealStarted = true;
      setDetailsVisible(true);
      window.dispatchEvent(new Event("home-details-reveal:start"));
    }, detailsRevealDelay);

    return () => {
      window.cancelAnimationFrame(revealFrame);
      window.clearTimeout(detailsTimer);
    };
  }, [canReveal]);

  return (
    <Container id="home">
      <motion.div
        className="wrap open-to-work"
        initial="hidden"
        animate={detailsVisible ? "visible" : "hidden"}
        variants={fadeVariants}
      >
        <span />
        <p>Open to Work</p>
      </motion.div>

      <motion.div
        className={`home-wrapper ${nameVisible ? "ready" : ""}`}
        initial="hidden"
        animate={detailsVisible ? "visible" : "hidden"}
        variants={wrapperVariants}
      >
        <NameSVG
          fill="#fff"
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: "block",
            width: "75%",
            height: "75%",
          }}
        />

        <motion.div className="intro-desc" variants={fadeVariants}>
          <motion.div className="intro-title" variants={fadeVariants}>
            <GoArrowDownRight />
            <p>I’m a Software Engineer</p>
          </motion.div>

          <motion.div className="intro-meta" variants={fadeVariants}>
            <span>4+ Years Experience</span>
            <GoDotFill />
            <span>Full-Stack</span>
            <GoDotFill />
            <span>Cloud</span>
            <GoDotFill />
            <span>AI/ML</span>
          </motion.div>
        </motion.div>
      </motion.div>

      <video
        loop
        autoPlay
        muted
        playsInline
        onContextMenu={disableImage}
        onDragStart={disableImage}
        src={bg}
      />
    </Container>
  );
}

const Container = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  .open-to-work {
    display: block;
    width: 100%;
    position: absolute;
    top: 35px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    user-select: none;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgb(29, 244, 129);
      display: inline-block;
      flex-shrink: 0;
      animation: blink 0.8s infinite ease-in-out;
    }

    p {
      font-size: 15px;
      font-weight: 300;
      color: #abababff;
    }
  }

  @keyframes blink {
    0% {
      opacity: 1;
      transform: scale(1);
    }

    50% {
      opacity: 0.5;
      transform: scale(0.8);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  video {
    position: absolute;
    width: 100%;
    height: 100vh;
    top: 0%;
    left: 0;
    right: 0;
    bottom: 0;
    object-fit: cover;
    z-index: 1;
    opacity: 0.2;
    pointer-events: none;
  }

  .home-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    position: relative;
    z-index: 2;
  }

  .intro-name {
    opacity: 0;
    transform: scale(0.86);
    transform-origin: center;
    transition:
      opacity 1s cubic-bezier(0.22, 1, 0.36, 1),
      transform 1s cubic-bezier(0.22, 1, 0.36, 1);
    will-change: opacity, transform;
  }

  .home-wrapper.ready .intro-name {
    opacity: 1;
    transform: scale(1);
  }

  .intro-desc {
    width: 100%;
    max-width: 620px;
    margin-top: 48px;
    margin-left: 400px;
  }

  .intro-title {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    svg {
      font-size: 30px;
      flex-shrink: 0;
      margin-top: -9px;
      color: #858585;
    }

    p {
      margin: 0;
      font-size: 18px;
      font-weight: 300;
      color: #858585;
      letter-spacing: 1.5px;
      line-height: 1.5;
      text-transform: uppercase;
    }
  }

  .intro-meta {
    margin-top: 12px;
    margin-left: 42px;

    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    font-size: 13px;
    font-weight: 300;
    color: #858585;

    text-transform: uppercase;
    letter-spacing: 1.2px;

    span {
      white-space: nowrap;
    }

    i {
      margin: 0 10px;
      color: #555;
      font-style: normal;
      font-size: 9px;
    }
  }

  @media (max-width: 915px) {
    .intro-desc {
      margin-left: 0;
      width: 100%;
      max-width: 620px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .intro-title {
      justify-content: center;
      width: 100%;
      text-align: center;

      svg {
        display: none;
      }

      p {
        text-align: center;
      }
    }

    .intro-meta {
      margin-left: 0;
      justify-content: center;
      width: 100%;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    .open-to-work {
      span {
        width: 6px;
        height: 6px;
      }

      p {
        font-size: 12px;
      }
    }

    .intro-desc {
      margin-top: 32px;
    }

    .intro-title {
      svg {
        display: none;
      }

      p {
        font-size: 15px;
      }
    }

    .intro-meta {
      margin-left: 0;
      margin-top: 10px;
      font-size: 10px;
      letter-spacing: 0.8px;

      i {
        margin: 0 7px;
      }
    }
  }
`;
